import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCategories } from "../lib/categoriesContext";
import { LEGAL_DISCLAIMER } from "../lib/categories";
import { ArticleCard } from "../components/ArticleCard";
import { ArrowRight, Sparkles, FileSearch, Megaphone, ShoppingBag } from "lucide-react";

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { categories } = useCategories();

  useEffect(() => {
    api.get("/articles", { params: { limit: 30 } }).then((r) => setArticles(r.data)).finally(() => setLoading(false));
  }, []);

  const hero = articles.find((a) => a.featured) || articles[0];
  const aLaUne = articles.filter((a) => a.slug !== hero?.slug).slice(0, 6);
  const mythBuster = articles.find((a) => a.template === "myth_buster");
  const moriweed = articles.filter((a) => a.category_slug === "moriweed").slice(0, 3);

  return (
    <div data-testid="home-page">
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          {hero && <ArticleCard article={hero} variant="hero" />}
        </div>
      </section>

      {aLaUne.length > 0 && (
        <section className="border-b border-[#E0D8CE] bg-[#4A7A3D] text-[#FAF8F3] relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
            <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="inline-block font-mono-label bg-[#C4944A] text-[#1A1A18] px-3 py-1.5 mb-3" data-testid="home-a-la-une-label">À LA UNE</span>
                <h2 className="font-display text-3xl md:text-5xl text-[#FAF8F3]">Ce qu'il faut lire</h2>
              </div>
              <Link to="/recherche" className="hidden md:inline font-mono-label text-[#FAF8F3] hover:text-[#C4944A]" data-testid="home-see-all">Tout voir →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              {aLaUne.slice(0, 3).map((a) => <ArticleCard key={a.slug} article={a} variant="onDark" />)}
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <div className="font-mono-label text-[#4A7A3D] mb-2">{categories.length} rubriques</div>
              <h2 className="font-display text-3xl md:text-5xl">Explorer le territoire</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border border-[#E0D8CE]">
            {categories.map((c, i) => (
              <Link key={c.slug} to={`/rubrique/${c.slug}`} className="p-6 md:p-8 border-r border-b border-[#E0D8CE] card-shift group relative" data-testid={`home-cat-card-${c.slug}`}>
                <span className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: c.accent_color || "#4A7A3D" }} />
                <div className="font-mono-label text-[#4A7A3D] mb-3">{String(i + 1).padStart(2, "0")} / {c.tagline}</div>
                <h3 className="font-display text-xl md:text-2xl leading-tight group-hover:text-[#4A7A3D]">{c.name}</h3>
                <p className="mt-3 text-sm text-[#4B4B48] line-clamp-2">{c.description}</p>
                <div className="mt-5 font-mono-label text-[#1A1A18] flex items-center gap-1 group-hover:text-[#4A7A3D]">Lire <ArrowRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A1A18] text-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="font-mono-label text-[#C4944A] mb-4">Weed Numbers</div>
            <div className="font-display text-7xl md:text-9xl text-[#C4944A] leading-none">53 000</div>
            <div className="font-display text-2xl md:text-3xl mt-4">études scientifiques</div>
          </div>
          <div className="md:col-span-7">
            <p className="font-quote text-2xl md:text-3xl leading-snug text-[#FAF8F3] mb-6">
              « Plus d'études scientifiques sur le cannabis que sur le café. La prohibition, elle, stagne depuis 1937. »
            </p>
            <p className="text-[#FAF8F3]/70 max-w-xl">Pourquoi la connaissance n'a-t-elle pas réformé le cadre légal mauricien&nbsp;?</p>
            <Link to="/article/53000-etudes-prohibition-stagne" className="inline-flex items-center gap-2 mt-6 border-b-2 border-[#C4944A] pb-1 font-mono-label text-[#C4944A] hover:text-[#FAF8F3] hover:border-[#FAF8F3]" data-testid="home-stat-cta">
              Lire l'analyse <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {mythBuster && (
        <section className="border-b border-[#E0D8CE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10">
            <div>
              <div className="font-mono-label text-[#4A7A3D] mb-3 flex items-center gap-2"><FileSearch size={14} /> Weed Files</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">Mythe ou réalité ?</h2>
              <p className="mt-5 text-lg text-[#4B4B48] max-w-md">On ouvre les dossiers, on confronte les idées reçues aux études. Sans complaisance, sans complotisme.</p>
            </div>
            <Link to={`/article/${mythBuster.slug}`} className="grid grid-cols-2 border-2 border-[#1A1A18] card-shift group" data-testid="home-myth-buster-cta">
              <div className="bg-[#FAF8F3] p-6 md:p-8 border-r-2 border-[#1A1A18]">
                <div className="font-mono-label text-[#C4944A] mb-3">Mythe</div>
                <p className="font-quote text-lg md:text-xl text-[#1A1A18] leading-snug line-clamp-5">« {mythBuster.myth || "Le cannabis mène inévitablement aux drogues dures."} »</p>
              </div>
              <div className="bg-[#1A1A18] text-[#FAF8F3] p-6 md:p-8">
                <div className="font-mono-label text-[#8FAF82] mb-3">Réalité</div>
                <p className="text-base md:text-lg leading-relaxed line-clamp-5">{mythBuster.reality || "L'écrasante majorité des consommateurs ne passent jamais à d'autres substances."}</p>
                <div className="mt-5 font-mono-label text-[#C4944A] group-hover:text-[#FAF8F3]">Voir l'analyse →</div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {moriweed.length > 0 && (
        <section className="border-b border-[#E0D8CE] bg-[#F5EFEB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="flex items-baseline justify-between mb-10">
              <div>
                <div className="font-mono-label text-[#4A7A3D] mb-2">MoriWeed</div>
                <h2 className="font-display text-3xl md:text-5xl">Moris pe baze</h2>
              </div>
              <Link to="/rubrique/moriweed" className="font-mono-label text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="home-moriweed-link">Toute la rubrique →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {moriweed.map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#4A7A3D] text-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="font-mono-label text-[#FAF8F3]/70 mb-3 flex items-center gap-2"><Megaphone size={14} /> Manifeste</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">Légalisation du cannabis<br />à l'île Maurice.</h2>
            <p className="mt-5 text-lg max-w-xl text-[#FAF8F3]/85">5 piliers, 5 études de cas, un document de référence pour ouvrir le débat public mauricien.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
            <Link to="/plaidoyer" className="bg-[#FAF8F3] text-[#1A1A18] px-8 py-4 font-mono-label hover:bg-[#C4944A] hover:text-[#1A1A18] transition-colors w-full md:w-auto text-center" data-testid="home-plaidoyer-cta">Lire notre plaidoyer →</Link>
            <span className="font-mono-label text-[#FAF8F3]/70 flex items-center gap-2"><Sparkles size={14} /> 17 octobre 2024</span>
          </div>
        </div>
      </section>

      {/* BOUTIQUE TEASER */}
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="font-mono-label text-[#4A7A3D] mb-3 flex items-center gap-2"><ShoppingBag size={14} /> Boutique</div>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">Porte le mouvement, finance le plaidoyer.</h2>
            <p className="mt-5 text-lg text-[#4B4B48] max-w-xl">T-shirts, hoodies, stickers, tote bags. 100 % des bénéfices financent l'impression et la diffusion du plaidoyer à Maurice.</p>
            <Link to="/boutique" className="inline-flex items-center gap-2 mt-6 border-b-2 border-[#1A1A18] pb-1 font-mono-label hover:text-[#4A7A3D] hover:border-[#4A7A3D]" data-testid="home-boutique-cta">
              Voir la boutique <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/3] bg-[#F5EFEB] border border-[#E0D8CE] flex items-center justify-center font-display text-7xl text-[#4A7A3D]">RBL</div>
          </div>
        </div>
      </section>

      {aLaUne.length > 3 && (
        <section className="border-b border-[#E0D8CE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="font-mono-label text-[#4A7A3D] mb-2">Plus d'articles</div>
            <h2 className="font-display text-3xl md:text-5xl mb-10">Continuer la lecture</h2>
            <div className="grid md:grid-cols-3 gap-10">
              <div className="md:col-span-2 space-y-0">
                {aLaUne.slice(3, 6).map((a) => <ArticleCard key={a.slug} article={a} variant="wide" />)}
              </div>
              <div>
                <div className="font-mono-label text-[#4B4B48] mb-3 pb-2 border-b-2 border-[#1A1A18]">À LIRE AUSSI</div>
                {aLaUne.slice(6, 12).map((a) => <ArticleCard key={a.slug} article={a} variant="compact" />)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-[#E0D8CE] bg-[#FAF8F3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="font-mono-label text-[#C4944A] mb-3">AVERTISSEMENT</div>
          <p className="font-quote text-xl md:text-2xl text-[#4B4B48] leading-snug">« {LEGAL_DISCLAIMER} »</p>
        </div>
      </section>
    </div>
  );
}
